/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { Popover } from "@material-ui/core";
import { Block, Delete } from "@material-ui/icons";
import * as Sentry from "@sentry/browser";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";
import { Button } from "ui/chakra/components/Button";

import { getFullscreenContainer } from "./useFullscreen";

import type { UserMessage } from "@spwn/types/firebase/firestore";

type Props = {
  target: React.RefObject<HTMLElement>;
  banTargetComment: Pick<UserMessage, "_id" | "uid"> | undefined;
  disableUserBlock: boolean;
  onClose: () => void;
  onClickBanUser: (comment: Pick<UserMessage, "_id" | "uid">) => Promise<void>;
  onClickBanComment: (idx: string) => Promise<void>;
};

const buttonStyle = css`
  padding: 8px 4px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 8px;
`;

const hoverStyle = css`
  width: 100%;
  justify-content: flex-start;
  &:hover:not(:disabled) {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

/**
 * 配信の管理者がコメントを BAN するためのメニュー。
 * 一般ユーザーの個人ブロックは StreamingCommentBlockDialog。
 */
export const StreamingCommentBanDialog: React.FC<Props> = (props) => {
  const { t } = useI18n();

  if (!props.banTargetComment) return null;

  return (
    <Popover
      open={!!props.banTargetComment}
      container={getFullscreenContainer()}
      onBackdropClick={props.onClose}
      anchorEl={props.target.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <div css={buttonStyle}>
        <Button
          size="sm"
          variant="ghost"
          disabled={props.disableUserBlock}
          onClick={async () => {
            if (!props.banTargetComment) return;
            // 書き込み失敗は未捕捉 rejection にせず Sentry に送り、finally で必ず閉じる
            // （ポップオーバー開きっぱなしを防ぐ）。
            try {
              await props.onClickBanUser(props.banTargetComment);
            } catch (e) {
              Sentry.captureException(e);
            } finally {
              props.onClose();
            }
          }}
          leftIcon={<Block />}
          css={hoverStyle}
        >
          {t("streaming.commentBan.blockUser")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={async () => {
            if (!props.banTargetComment?._id) return;
            try {
              await props.onClickBanComment(props.banTargetComment._id);
            } catch (e) {
              Sentry.captureException(e);
            } finally {
              props.onClose();
            }
          }}
          leftIcon={<Delete />}
          css={hoverStyle}
        >
          {t("streaming.commentBan.deleteComment")}
        </Button>
      </div>
    </Popover>
  );
};
